import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Card,
  CardContent
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import PersonIcon from '@mui/icons-material/Person';
import { useLanguage } from '../context/LanguageContext';
import { useTeluguYear } from '../context/TeluguYearContext';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { getKrishnaGrandhamInfo, getTotalExpectedPannams, countPannamsInGrandham, krishnaYajurvedaTreeData, categorizeKrishnaVatu } from '../utils/krishnaYajurvedaUtils';
import { ar } from 'date-fns/locale';

const RegistrationReport = () => {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const { teluguYearTelugu, teluguYearSanskrit } = useTeluguYear();
  const [vatus, setVatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupBy, setGroupBy] = useState('pathasala'); // 'pathasala' or 'pariksha_type'
  const [krishnaFilter, setKrishnaFilter] = useState('all'); // Krishna subcategory filter
  const [expandAll, setExpandAll] = useState(false); // Track expand/collapse all state - DEFAULT COLLAPSED
  const [expandedGroups, setExpandedGroups] = useState(new Set()); // Track individual accordion states

  const currentYear = lang === 'telugu' ? teluguYearTelugu : teluguYearSanskrit;

  const labels = {
    telugu: {
      notCompleted: 'నమోదు పూర్తి కాలేదు'
    },
    sanskrit: {
      notCompleted: 'पञ्जीकरण अपूर्णम्'
    }
  };

  useEffect(() => {
    if (teluguYearTelugu && teluguYearSanskrit) {
      fetchVatus();
    }
  }, [teluguYearTelugu, teluguYearSanskrit]);

  // Reset Krishna filter when grouping changes
  useEffect(() => {
    if (groupBy !== 'pariksha_type') {
      setKrishnaFilter('all');
    }
  }, [groupBy]);

  // Handle individual accordion expansion/collapse
  const handleAccordionChange = (groupKey, isExpanded) => {
    const newExpandedGroups = new Set(expandedGroups);
    if (isExpanded) {
      newExpandedGroups.add(groupKey);
    } else {
      newExpandedGroups.delete(groupKey);
    }
    setExpandedGroups(newExpandedGroups);
  };

  const fetchVatus = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/vatu?include_reward=true');
      const data = await res.json();
      
      if (res.ok && data.success) {
        // Filter only current year vatus
        const currentYearVatus = data.data.filter(v => 
          v.year === teluguYearTelugu || v.year === teluguYearSanskrit
        );
        setVatus(currentYearVatus);
      }
    } catch (error) {
      console.error('Error fetching vatus:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to deep merge two grandham objects
  const mergeGrandhas = (previous, current) => {
    if (!previous && !current) return {};
    if (!previous) return current;
    if (!current) return previous;
    
    const merged = { ...previous };
    
    // Deep merge current into previous
    Object.keys(current).forEach(key => {
      if (typeof current[key] === 'object' && current[key] !== null && !Array.isArray(current[key])) {
        merged[key] = mergeGrandhas(merged[key], current[key]);
      } else {
        merged[key] = current[key];
      }
    });
    
    return merged;
  };

  // Helper function to get selection status for a grandham: 'Complete'|'Progress'|'NotStarted'
  const getSelectionStatus = (grandhamObj, keys) => {
    if (!grandhamObj) return 'NotStarted';

    // If grandhamObj is wrapped in an array, unwrap it
    if (Array.isArray(grandhamObj) && grandhamObj.length > 0 && typeof grandhamObj[0] === 'object') {
      grandhamObj = grandhamObj[0];
    }

    if (typeof grandhamObj !== 'object') return 'NotStarted';

    const treeForLang = krishnaYajurvedaTreeData[lang] || krishnaYajurvedaTreeData.telugu;

    const statuses = keys.map(key => {
      const gh = grandhamObj[key];
      const expected = treeForLang && treeForLang[key];

      // If no tree entry, interpret _selected/_autoSelected as Complete, any child as Progress
      if (!expected) {
        if (!gh) return 'NotStarted';
        if (gh._selected === true || gh._autoSelected === true || gh === true) return 'Complete';
        // any child keys (excluding metadata) => progress
        if (typeof gh === 'object' && Object.keys(gh).some(k => !k.startsWith('_'))) return 'Progress';
        return 'NotStarted';
      }

      // If grandha-level selection exists, it's Complete
      if (gh && (gh._selected === true || gh._autoSelected === true)) return 'Complete';

      if (!gh || typeof gh !== 'object') return 'NotStarted';

      let totalExpected = 0;
      let totalPresent = 0;

      for (const sectionName of Object.keys(expected)) {
        const numbers = expected[sectionName] || [];
        totalExpected += numbers.length;

        // If section object exists and is marked selected, count all as present
        if (gh[sectionName] && gh[sectionName]._selected === true) {
          totalPresent += numbers.length;
          continue;
        }

        // If section exists as object, count individual entries
        if (gh[sectionName] && typeof gh[sectionName] === 'object') {
          for (const num of numbers) {
            if (gh[sectionName][num] !== undefined && gh[sectionName][num] !== null) totalPresent++;
          }
        } else {
          // Some payloads place "main" numbers directly under the grandha (e.g., ఆరణ్యకభాగము)
          for (const num of numbers) {
            if (gh[num] !== undefined && gh[num] !== null) totalPresent++;
          }
        }
      }

      if (totalExpected === 0) return 'NotStarted';
      if (totalPresent === 0) return 'NotStarted';
      if (totalPresent === totalExpected) return 'Complete';
      return 'Progress';
    });

    // Aggregate statuses across keys
    const allComplete = statuses.every(s => s === 'Complete');
    const allNotStarted = statuses.every(s => s === 'NotStarted');
    if (allComplete) return 'Complete';
    if (allNotStarted) return 'NotStarted';
    return 'Progress';
  };

  // Group vatus based on selected grouping
  const groupVatus = () => {
    if (groupBy === 'pathasala') {
      const grouped = {};
      vatus.forEach(vatu => {
        const key = vatu.pathasala_id || 'Unknown';
        if (!grouped[key]) {
          grouped[key] = {
            vatus: [],
            pathasala_name: vatu.pathasala_name || 'Unknown'
          };
        }
        grouped[key].vatus.push(vatu);
      });
      return grouped;
    } else if (groupBy === 'pariksha_type') {
      const grouped = {};
      vatus.forEach(vatu => {
        const types = Array.isArray(vatu.pariksha_type) ? vatu.pariksha_type : [vatu.pariksha_type];
        types.forEach(type => {
          if (!type) return;
          if (!grouped[type]) grouped[type] = [];
          grouped[type].push(vatu);
        });
      });
      return grouped;
    }
    return {};
  };

  // Filter vatus based on selected filter (supports all pariksha types)
  const filterKrishnaVatus = (vatus) => {
    if (krishnaFilter === 'all') return vatus;
    
    // Krishna Yajurveda completion levels
    const krishnaCategories = ['brahmana', 'aranyaka', 'samhita', 'khanda', 'moolam', 'padamu', 'kramamu', 'saamaanya_kramamu', 'jata', 'saamaanya_ghanamu', 'vishesha_kramamu', 'vishesha_ghanamu'];
    
    return vatus.filter(vatu => {
      const types = Array.isArray(vatu.pariksha_type) ? vatu.pariksha_type : [vatu.pariksha_type];
      
      // If filtering by completed status - show only vatus where exam is completed (examReward exists and is not "exam not completed")
      if (krishnaFilter === 'completed') {
        return vatu.examReward !== undefined && vatu.examReward !== null && vatu.examReward !== "పరీక్ష పూర్తి కాలేదు";
      }
      
      // If filtering by Krishna category (completion levels)
      if (krishnaCategories.includes(krishnaFilter)) {
        // Only apply to vatus with krishna_yajurveda
        if (!types.includes('krishna_yajurveda')) return false;
        
        const category = categorizeKrishnaVatu(vatu, lang);
        console.log('Vatu:', vatu.vatu_name, 'Krishna Category:', category, 'Filter:', krishnaFilter);
        return category === krishnaFilter;
      }
      
      // If filtering by pariksha type (lakshanam, sanskrit, etc.)
      if (krishnaFilter === 'lakshanam') {
        // Check if vatu has lakshanam, lakshanam_kanthastham, or lakshanam_vyakhyanam
        return types.includes('lakshanam') || types.includes('lakshanam_kanthastham') || types.includes('lakshanam_vyakhyanam');
      }

      //Only Sanskrit not with Krishna Yajurveda
      if(krishnaFilter === 'sanskrit') {
        return types.includes('sanskrit') && !types.includes('krishna_yajurveda');
      }
      
      // For other pariksha types, check if vatu has that type
      return types.includes(krishnaFilter);
    });
  };

  const getParikshaTypeLabel = (type) => {
    const labels = {
      krishna_yajurveda: { telugu: 'కృష్ణ యజుర్వేదం', sanskrit: 'कृष्ण यजुर्वेदम्' },
      lakshanam: { telugu: 'లక్షణం', sanskrit: 'लक्षणम्' },
      lakshanam_kanthastham: { telugu: 'లక్షణం కంఠస్థం', sanskrit: 'लक्षणम् कण्ठस्थम्' },
      lakshanam_vyakhyanam: { telugu: 'లక్షణం వ్యాఖ్యానం', sanskrit: 'लक्षणम् व्याख्यानम्' },
      sanskrit: { telugu: 'సంస్కృతం', sanskrit: 'संस्कृतम्' },
      vedadhyayi_sahityam: { telugu: 'వేదాధ్యాయి సాహిత్యం', sanskrit: 'वेदाध्यायी साहित्यम्' },
      vedadhyeta_sahityam: { telugu: 'వేదాధ్యేత సాహిత్యం', sanskrit: 'वेदाध्येत साहित्यम्' },
      sahityam: { telugu: 'సాహిత్యం', sanskrit: 'साहित्यम्' }
    };
    return labels[type]?.[lang] || type;
  };

  const getKrishnaFilterLabel = (filter) => {
    const labels = {
      all: { telugu: 'అన్నీ', sanskrit: 'सर्वाणि' },
      completed: { telugu: 'పరీక్ష పూర్తి', sanskrit: 'परीक्षा पूर्णम्' },
      khanda: { telugu: 'ఖండ', sanskrit: 'खण्ड' },
      brahmana: { telugu: 'బ్రాహ్మణము', sanskrit: 'ब्राह्मणम्' },
      aranyaka: { telugu: 'ఆరణ్యకము', sanskrit: 'आरण्यकम्' },
      samhita: { telugu: 'సంహిత', sanskrit: 'संहित' },
      moolam: { telugu: 'మూలము', sanskrit: 'मूलम्' },
      padamu: { telugu: 'పదము', sanskrit: 'पदम्' },
      kramamu: { telugu: 'క్రమము', sanskrit: 'क्रमम्' },
      saamaanya_kramamu: { telugu: 'క్రమాంతం - పట్టా', sanskrit: 'क्रमान्थम् - पट्टा' },
      jata: { telugu: 'జటా', sanskrit: 'जटा' },
      saamaanya_ghanamu: { telugu: 'ఘనాంతం - పట్టా', sanskrit: 'घनान्थम् - पट्टा' },
      vishesha_kramamu: { telugu: 'విశేష క్రమాంతం - పట్టా', sanskrit: 'विशेष क्रमान्थम् - पट्टा' },
      vishesha_ghanamu: { telugu: 'విశేష ఘనాంతం - పట్టా', sanskrit: 'विशेष घनान्थम् - पट्टा' },
      // Additional pariksha types
      lakshanam: { telugu: 'లక్షణం', sanskrit: 'लक्षणम्' },
      sanskrit: { telugu: 'సంస్కృతం', sanskrit: 'संस्कृतम्' },
      vedadhyayi_sahityam: { telugu: 'వేదాధ్యాయి సాహిత్యం', sanskrit: 'वेदाध्यायी साहित्यम्' },
    };
    return labels[filter]?.[lang] || filter;
  };

  const groupedVatus = groupVatus();

  // Filter to show only relevant accordion when filter is applied
  const filteredGroupedVatus = React.useMemo(() => {
    if (groupBy === 'pariksha_type' && krishnaFilter !== 'all') {
      const krishnaCategories = ['brahmana', 'aranyaka', 'samhita','khanda', 'moolam', 'padamu', 'kramamu', 'saamaanya_kramamu', 'jata', 'saamaanya_ghanamu', 'vishesha_kramamu', 'vishesha_ghanamu'];
      
      // If filtering by completed status, show all accordions - filtering happens in displayVatus
      if (krishnaFilter === 'completed') {
        return groupedVatus;
      }
      
      // If filtering by Krishna completion levels, show only krishna_yajurveda accordion
      if (krishnaCategories.includes(krishnaFilter)) {
        return groupedVatus.krishna_yajurveda 
          ? { krishna_yajurveda: groupedVatus.krishna_yajurveda }
          : {};
      }
      
      // If filtering by lakshanam, show lakshanam, lakshanam_kanthastham, and lakshanam_vyakhyanam accordions
      if (krishnaFilter === 'lakshanam') {
        const lakshanamGroups = {};
        if (groupedVatus.lakshanam) lakshanamGroups.lakshanam = groupedVatus.lakshanam;
        if (groupedVatus.lakshanam_kanthastham) lakshanamGroups.lakshanam_kanthastham = groupedVatus.lakshanam_kanthastham;
        if (groupedVatus.lakshanam_vyakhyanam) lakshanamGroups.lakshanam_vyakhyanam = groupedVatus.lakshanam_vyakhyanam;
        return lakshanamGroups;
      }
      
      // For other pariksha types, show only that specific accordion
      return groupedVatus[krishnaFilter] 
        ? { [krishnaFilter]: groupedVatus[krishnaFilter] }
        : {};
    }
    return groupedVatus;
  }, [groupBy, krishnaFilter, groupedVatus]);

  const displayGroupedVatus = filteredGroupedVatus;

  // Sync expanded groups when expandAll changes
  useEffect(() => {
    if (expandAll) {
      // Expand all groups - will be populated on next render
      const groupKeys = Object.keys(displayGroupedVatus);
      setExpandedGroups(new Set(groupKeys));
    } else {
      // Collapse all groups
      setExpandedGroups(new Set());
    }
  }, [expandAll]);

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Paper elevation={0} sx={{ p: 4, mb: 3, background: 'linear-gradient(135deg, #7f4b2d 0%, #d0b996 100%)', borderRadius: 3 }}>
        <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700, textAlign: 'center', mb: 1 }}>
          {lang === 'telugu' ? 'పరీక్ష నమోదు నివేదిక' : 'परीक्षा पञ्जीकरण निवेदिका'}
        </Typography>
        <Typography variant="body1" sx={{ color: '#e3f2fd', textAlign: 'center' }}>
          {currentYear} {lang === 'telugu' ? 'సంవత్సరం' : 'संवत्सरम्'}
        </Typography>
      </Paper>

      {/* Grouping Controls */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
          <Grid item xs={12} sm={12} md={6} sx={{ width: '100%' }}>
            <FormControl fullWidth sx={{ width: '100%' }}>
              <InputLabel>{lang === 'telugu' ? 'వర్గీకరణ' : 'वर्गीकरणम्'}</InputLabel>
              <Select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
                label={lang === 'telugu' ? 'వర్గీకరణ' : 'वर्गीकरणम्'}
                sx={{ width: '100%' }}
              >
                <MenuItem value="pathasala">
                  {lang === 'telugu' ? 'పాఠశాల' : 'पाठशाला'}
                </MenuItem>
                <MenuItem value="pariksha_type">
                  {lang === 'telugu' ? 'పరీక్ష రకం' : 'परीक्षा प्रकारः'}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {groupBy === 'pariksha_type' && (
            <Grid item xs={12} sm={12} md={6} sx={{ width: '100%' }}>
              <FormControl fullWidth sx={{ width: '100%' }}>
                <InputLabel>{lang === 'telugu' ? 'పరీక్ష రకం' : 'परीक्षा प्रकारः'}</InputLabel>
                <Select
                  value={krishnaFilter}
                  onChange={(e) => setKrishnaFilter(e.target.value)}
                  label={lang === 'telugu' ? 'పరీక్ష రకం' : 'परीक्षा प्रकारः'}
                  sx={{ width: '100%' }}
                >
                  <MenuItem value="all">{getKrishnaFilterLabel('all')}</MenuItem>
                  <MenuItem value="completed">{getKrishnaFilterLabel('completed')}</MenuItem>
                  <MenuItem value="khanda">{getKrishnaFilterLabel('khanda')}</MenuItem>
                  <MenuItem value="brahmana">{getKrishnaFilterLabel('brahmana')}</MenuItem>
                  <MenuItem value="aranyaka">{getKrishnaFilterLabel('aranyaka')}</MenuItem>
                  <MenuItem value="samhita">{getKrishnaFilterLabel('samhita')}</MenuItem>
                  <MenuItem value="moolam">{getKrishnaFilterLabel('moolam')}</MenuItem>
                  <MenuItem value="padamu">{getKrishnaFilterLabel('padamu')}</MenuItem>
                  <MenuItem value="kramamu">{getKrishnaFilterLabel('kramamu')}</MenuItem>
                  <MenuItem value="saamaanya_kramamu">{getKrishnaFilterLabel('saamaanya_kramamu')}</MenuItem>
                  <MenuItem value="jata">{getKrishnaFilterLabel('jata')}</MenuItem>
                  <MenuItem value="saamaanya_ghanamu">{getKrishnaFilterLabel('saamaanya_ghanamu')}</MenuItem>
                  <MenuItem value="vishesha_kramamu">{getKrishnaFilterLabel('vishesha_kramamu')}</MenuItem>
                  <MenuItem value="vishesha_ghanamu">{getKrishnaFilterLabel('vishesha_ghanamu')}</MenuItem>
                  <MenuItem value="lakshanam">{getKrishnaFilterLabel('lakshanam')}</MenuItem>
                  <MenuItem value="sanskrit">{getKrishnaFilterLabel('sanskrit')}</MenuItem>
                  <MenuItem value="vedadhyayi_sahityam">{getKrishnaFilterLabel('vedadhyayi_sahityam')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Summary Card */}
      <Card elevation={2} sx={{ mb: 3, background: '#f5f5f5' }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {lang === 'telugu' ? 'మొత్తం నమోదులు' : 'कुल पञ्जीकरणानि'}: {vatus.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {lang === 'telugu' ? 'వర్గాలు' : 'वर्गाः'}: {Object.keys(displayGroupedVatus).length}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box
              onClick={() => setExpandAll(!expandAll)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 2,
                py: 1,
                borderRadius: 1,
                cursor: 'pointer',
                backgroundColor: '#1976d2',
                color: '#fff',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: '#1565c0',
                }
              }}
            >
              {expandAll ? (
                <>
                  <UnfoldLessIcon sx={{ fontSize: 20 }} />
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    {lang === 'telugu' ? 'కుదించు' : 'संकोचयतु'}
                  </Typography>
                </>
              ) : (
                <>
                  <UnfoldMoreIcon sx={{ fontSize: 20 }} />
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    {lang === 'telugu' ? 'విస్తరించు' : 'विस्तारयतु'}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Grouped Vatus Display */}
      {Object.entries(displayGroupedVatus).map(([groupKey, groupData]) => {
        // Handle both pariksha_type (array) and pathasala (object with vatus array) grouping
        const groupVatus = groupBy === 'pathasala' ? groupData.vatus : groupData;
        const displayVatus = groupBy === 'pariksha_type' && krishnaFilter !== 'all'
          ? filterKrishnaVatus(groupVatus) 
          : groupVatus;
        const isExpanded = expandedGroups.has(groupKey);

        if (displayVatus.length === 0) return null;

        const headerLabel = groupBy === 'pariksha_type' 
          ? getParikshaTypeLabel(groupKey) 
          : groupData.pathasala_name;

        return (
          <Accordion 
            key={groupKey} 
            expanded={isExpanded}
            onChange={(e, newIsExpanded) => handleAccordionChange(groupKey, newIsExpanded)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {headerLabel} ({displayVatus.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {displayVatus.map((vatu) => (
                  <Grid item xs={12} sm={6} md={4} key={vatu._id}>
                    <Card elevation={1} sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box
                            onClick={() => navigate(`/vatuvu?vatu_id=${vatu.vatu_id}`)}
                            sx={{
                              width: 60,
                              height: 60,
                              borderRadius: '50%',
                              overflow: 'hidden',
                              border: '3px solid #7f4b2d',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              mr: 2,
                              flexShrink: 0,
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                                borderColor: '#4caf50',
                                transform: 'scale(1.05)'
                              }
                            }}
                          >
                            <img
                              src={vatu.photo_download_link || '/assets/default-avatar.png'}
                              alt={vatu.vatu_name}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: vatu.photo_object_config?.fit || 'cover',
                                objectPosition: vatu.photo_object_config?.position || 'center'
                              }}
                            />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {vatu.vatu_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {vatu.vatu_id}
                            </Typography>
                            {!vatu.completed && (
                              <Box
                                sx={{
                                  mt: 0.5,
                                  display: 'inline-block',
                                  backgroundColor: 'rgba(211, 47, 47, 0.1)',
                                  border: '1px solid rgb(211, 47, 47)',
                                  borderRadius: '4px',
                                  px: 1,
                                  py: 0.25
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'rgb(211, 47, 47)',
                                    fontWeight: 600,
                                    fontSize: '0.65rem'
                                  }}
                                >
                                  {labels[lang].notCompleted}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>

                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {lang === 'telugu' ? 'పరీక్షలు' : 'परीक्षाः'}:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {(Array.isArray(vatu.pariksha_type) ? vatu.pariksha_type : [vatu.pariksha_type]).map((type, idx) => {
                              // Special handling for krishna_yajurveda - show grandham and pannam count
                              if (type === 'krishna_yajurveda') {
                                const grandhamInfos = getKrishnaGrandhamInfo(vatu, lang);
                                if (grandhamInfos.length > 0) {
                                  return (
                                    <Box key={idx} sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                      {grandhamInfos.map((info, gIdx) => info.completionType !== "previous-complete" ? (
                                        <Chip
                                          key={gIdx}
                                          label={`${info.grandhamName} - ${info.pannams} ${lang === 'telugu' ? 'పన్నాలు' : 'पनाम्'}`}
                                          size="small"
                                          color="primary"
                                          variant="outlined"
                                        />
                                      ) : null)}
                                    </Box>
                                  );
                                }
                              }
                              // Default display for other pariksha types
                              return (
                                <Chip
                                  key={idx}
                                  label={getParikshaTypeLabel(type)}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              );
                            })}
                          </Box>
                        </Box>

                        {vatu.examReward !== undefined && vatu.examReward !== null && (
                          <Box sx={{ mb: 1, p: 1, backgroundColor: '#e8f5e9', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              {lang === 'telugu' ? 'పరీక్ష పురస్కారం' : 'परीक्षा पुरस्कारम्'}:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32', mt: 0.5 }}>
                              ₹ {vatu.examReward === "పరీక్ష పూర్తి కాలేదు" ? vatu.examReward : vatu.examReward +"(పురస్కారం) + ₹ "+ (vatu.expenseAmount || 0) +"(చార్జీలు) = ₹ "+ ( vatu.reward || 0) }
                            </Typography>
                          </Box>
                        )}

                        {groupBy !== 'pathasala' && (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                            {lang === 'telugu' ? 'పాఠశాల' : 'पाठशाला'}: {vatu.pathasala_name}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {Object.keys(displayGroupedVatus).length === 0 && (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {lang === 'telugu' ? 'నమోదులు లేవు' : 'पञ्जीकरणानि न सन्ति'}
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default RegistrationReport;
